import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Lock, Clock, BadgeCheck, ShieldCheck } from 'lucide-react';
import PersonaCard from '../components/PersonaCard.jsx';
import LanguageSwitch from '../components/LanguageSwitch.jsx';
import { PAIR_PERSONAS, findPair } from '../lib/pairPersonas.js';
import { t } from '../lib/i18n.js';
import {
  captureAttribution,
  trackPersonaSelected,
  grantConsent,
  trackCallbackRequested
} from '../lib/analytics.js';
import { readSession, writeSession } from '../lib/session.js';
import { requestCallback } from '../lib/api.js';

const PHONE_RE = /^[+]?[\d\s().-]{10,20}$/;
const NAME_RE = /^[A-Za-z][A-Za-z'\- ]{0,39}$/;

// Weight-loss landing copy — this page is the AD destination, so its messaging is
// about LOSING WEIGHT specifically (not the generic vitamd.health home page).
// Kept local (not in shared i18n) so the home page + men's-health page are
// untouched. Bilingual because the ads run EN + ES.
const WL = {
  en: {
    eyebrow: 'VitaMD · Weight Loss',
    title: 'Lose the weight. Get real answers first.',
    sub: 'Lose weight your way — through medication like GLP-1s, or through nutrition, movement, and sleep. Two AI guides help you understand your own body and what actually works for you, then connect you with a licensed provider. Educational, no pressure.',
    choose: 'Choose your two-person guide — tap a card to hear them',
    journeyTitle: 'Let’s talk about your goals',
    journeySub: 'Drop your name and number and your guides will call you in about a minute to talk through your options — medication and lifestyle. Educational only — a licensed provider makes any medical decisions.',
    howTitle: 'How it works',
    steps: [
      ['01', 'Pick your guides', 'Choose the duo you vibe with — tap to hear a preview.'],
      ['02', 'They call you', 'In about a minute, your two AI guides call to talk through your options — medication, nutrition, movement, sleep — and what fits you.'],
      ['03', 'A provider takes over', 'If it’s a fit, a licensed provider handles the medical side — evaluation, prescription, pharmacy.']
    ],
    valTitle: 'Weight loss, your way',
    vals: [
      ['Private & judgment-free', 'Talk through weight loss on your terms. Your information stays private.'],
      ['Answers in a minute', 'No waiting rooms. Your guides call you back fast, any hour, day or night.'],
      ['Licensed providers', 'Real, licensed providers make every medical decision and prescription — the AI is educational.']
    ]
  },
  es: {
    eyebrow: 'VitaMD · Pérdida de peso',
    title: 'Baja de peso. Primero, respuestas reales.',
    sub: 'Baja de peso a tu manera — con medicamentos como los GLP-1, o con nutrición, ejercicio y sueño. Dos guías de IA te ayudan a entender tu propio cuerpo y qué funciona para ti, y luego te conectan con un proveedor con licencia. Educativo, sin presión.',
    choose: 'Elige a tus dos guías — toca una tarjeta para escucharlos',
    journeyTitle: 'Hablemos de tus metas',
    journeySub: 'Deja tu nombre y número y tus guías te llamarán en aproximadamente un minuto para hablar de tus opciones — medicamentos y estilo de vida. Solo educativo — un proveedor con licencia toma cualquier decisión médica.',
    howTitle: 'Cómo funciona',
    steps: [
      ['01', 'Elige a tus guías', 'Escoge la pareja con la que conectes — toca para escuchar una muestra.'],
      ['02', 'Te llaman', 'En aproximadamente un minuto, tus dos guías de IA te llaman para hablar de tus opciones — medicamentos, nutrición, ejercicio y sueño — y qué encaja contigo.'],
      ['03', 'Un proveedor toma el control', 'Si encaja, un proveedor con licencia se encarga de lo médico — evaluación, receta, farmacia.']
    ],
    valTitle: 'Bajar de peso, a tu manera',
    vals: [
      ['Privado y sin juicios', 'Habla de bajar de peso a tu manera. Tu información es privada.'],
      ['Respuestas en un minuto', 'Sin salas de espera. Tus guías te devuelven la llamada rápido, a cualquier hora.'],
      ['Proveedores con licencia', 'Proveedores reales con licencia toman cada decisión médica y receta — la IA es educativa.']
    ]
  }
};

// Friendly, human-readable copy for the error CODES the backend returns
// (api.js throws Error(body.error)). Public visitors should never see a
// raw code like "consent_required". Bilingual to match the EN/ES landing.
const ERROR_COPY = {
  en: {
    consent_required: 'Please provide your consent to receive a call.',
    invalid_phone: 'Please enter a valid phone number.',
    invalid_persona: 'Please pick your two-person guide above first.',
    invalid_language: 'Please choose a language and try again.',
    dispatch_failed: 'We couldn’t place your call just now. Please try again in a moment.',
    default: 'Something went wrong. Please try again.'
  },
  es: {
    consent_required: 'Por favor, da tu consentimiento para recibir una llamada.',
    invalid_phone: 'Por favor, ingresa un número de teléfono válido.',
    invalid_persona: 'Primero elige a tus dos guías arriba.',
    invalid_language: 'Por favor, elige un idioma e inténtalo de nuevo.',
    dispatch_failed: 'No pudimos hacer la llamada en este momento. Inténtalo de nuevo en un momento.',
    default: 'Algo salió mal. Por favor, inténtalo de nuevo.'
  }
};

function friendlyError(code, lang) {
  const table = ERROR_COPY[lang] || ERROR_COPY.en;
  return table[code] || table.default || ERROR_COPY.en.default;
}

// VitaMD two-AI landing — the LIVE pairs page. This is a duplicate of Start.jsx
// (the single-voice men's-health page, which stays untouched). Key differences:
//   - picker shows three AI-PAIR cards (pairPersonas.js) instead of single voices
//   - the chosen pair is sent to the server as `pair_id` (api.js passthrough)
//   - NO coming-soon overlay: this page is the new live entry point
// It reuses PersonaCard, LanguageSwitch, the two-checkbox consent block, and the
// trust strip verbatim so the two pages share look-and-feel.
export default function VitamdStart() {
  const navigate = useNavigate();
  const [lang, setLang] = useState('en');
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [prefill, setPrefill] = useState({ firstName: '', phone: '' });
  const formRef = useRef(null);

  useEffect(() => {
    captureAttribution();
    // Recruit-study deep-link: ?study=<id>&ph=<phone>&fn=<first_name> arrives
    // from the screener form. Persist studyId; phone + first name are prefills.
    const params = new URLSearchParams(window.location.search);
    const study = params.get('study');
    if (study) {
      writeSession({
        studyId: study,
        prefillPhone: params.get('ph') || undefined,
        prefillFirstName: params.get('fn') || undefined
      });
    }
    const s = readSession();
    if (s.language) setLang(s.language);
    if (s.pairId) setSelected(s.pairId);
    if (s.prefillPhone || s.prefillFirstName) {
      setPrefill({
        firstName: s.prefillFirstName || '',
        phone: s.prefillPhone || ''
      });
    }
  }, []);

  const pair = selected ? findPair(selected) : null;
  const c = WL[lang] || WL.en; // weight-loss landing copy for the active language
  // Resolve a {en,es} copy field (or plain string) to the active language.
  const L = (v) => (v && typeof v === 'object' ? (v[lang] || v.en) : v);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset
  } = useForm({ mode: 'onChange', defaultValues: { firstName: '', phone: '', callConsent: false, textConsent: false } });

  // Prefill once the recruit-screener hint state lands.
  useEffect(() => {
    if (prefill.firstName || prefill.phone) {
      reset({ firstName: prefill.firstName, phone: prefill.phone, callConsent: false, textConsent: false });
    }
  }, [prefill.firstName, prefill.phone, reset]);

  const onSelect = (p) => {
    setSelected(p.id);
    trackPersonaSelected(p);
    writeSession({ pairId: p.id, language: lang });
    reset({
      firstName: prefill.firstName || '',
      phone: prefill.phone || '',
      callConsent: false,
      textConsent: false
    });
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const onSubmit = async (values) => {
    if (!selected) return;
    setApiError(null);
    setSubmitting(true);
    try {
      grantConsent();
      trackCallbackRequested({ personaId: selected, languageCode: lang });
      const session = readSession();
      await requestCallback({
        pairId: selected,
        phone: values.phone,
        languageCode: lang,
        callConsent: values.callConsent,
        textConsent: values.textConsent,
        consentAt: new Date().toISOString(),
        firstName: values.firstName.trim(),
        studyId: session.studyId
      });
      writeSession({
        pairId: selected,
        language: lang,
        firstName: values.firstName.trim(),
        submittedAt: new Date().toISOString()
      });
      navigate('/connect');
    } catch (err) {
      // err.message is the backend error CODE (or a network message); map
      // known codes to friendly copy, fall back to a generic message.
      setApiError(friendlyError(err.message, lang));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* ─── Hero + Pair picker ─────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 py-12 md:py-20">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.3em] text-alfred-gold">
            {c.eyebrow}
          </p>
          <h1 className="mt-4 text-4xl md:text-6xl font-serif leading-tight">
            {c.title}
          </h1>
          <p className="mt-5 text-lg text-stone-300 max-w-xl">
            {c.sub}
          </p>
          <div className="mt-6">
            <LanguageSwitch
              value={lang}
              langs={['en', 'es']}
              onChange={(c) => {
                setLang(c);
                writeSession({ language: c });
              }}
            />
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-sm uppercase tracking-[0.25em] text-stone-400 mb-4">
            {c.choose}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PAIR_PERSONAS.map((p) => (
              <PersonaCard
                key={p.id}
                persona={p}
                lang={lang}
                tagline={L(p.tagline)}
                blurb={L(p.blurb)}
                cta={L(p.cta)}
                selected={selected === p.id}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>

        {/* ─── Inline form — visible once a pair is picked ───────────────── */}
        {pair && (
          <section ref={formRef} className="mt-14 max-w-xl">
            <p className="text-xs uppercase tracking-[0.25em] text-alfred-gold">
              {pair.displayName}
            </p>
            <h2 className="mt-2 text-2xl md:text-3xl font-serif leading-tight">
              {c.journeyTitle}
            </h2>
            <p className="mt-3 text-stone-300">{c.journeySub}</p>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-5">
              <div>
                <label className="field-label" htmlFor="firstName">
                  {t(lang, 'firstName')}
                </label>
                <input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  placeholder={t(lang, 'firstNamePh')}
                  className="field-input"
                  {...register('firstName', {
                    required: true,
                    pattern: NAME_RE,
                    maxLength: 40
                  })}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-400">
                    Please enter a valid first name.
                  </p>
                )}
              </div>

              <div>
                <label className="field-label" htmlFor="phone">
                  {t(lang, 'phone')}
                </label>
                <input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder={t(lang, 'phonePh')}
                  className="field-input"
                  {...register('phone', { required: true, pattern: PHONE_RE })}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-400">
                    Please enter a valid phone number.
                  </p>
                )}
              </div>

              <label className="flex items-start gap-3 text-sm text-stone-300">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-stone-600 bg-stone-900 text-alfred-gold focus:ring-alfred-gold"
                  {...register('callConsent', { required: true })}
                />
                <span>{t(lang, 'callConsentLabel')}</span>
              </label>
              {errors.callConsent && (
                <p className="-mt-2 text-sm text-red-400">
                  We can't call without your consent.
                </p>
              )}

              <label className="flex items-start gap-3 text-sm text-stone-300">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-stone-600 bg-stone-900 text-alfred-gold focus:ring-alfred-gold"
                  {...register('textConsent')}
                />
                <span>{t(lang, 'textConsentLabel')}</span>
              </label>

              {apiError && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {apiError}
                </div>
              )}

              <button
                type="submit"
                className="btn-primary w-full sm:w-auto"
                disabled={!isValid || submitting}
              >
                {submitting ? t(lang, 'submitting') : t(lang, 'submit')}
              </button>
            </form>
          </section>
        )}
      </section>

      {/* ─── How it works (weight-loss funnel) ──────────────────────────── */}
      <section className="border-t border-stone-800">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <h2 className="text-sm uppercase tracking-[0.25em] text-stone-400">
            {c.howTitle}
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {c.steps.map(([n, title, body]) => (
              <div key={n} className="card">
                <p className="text-alfred-gold font-serif text-xl">{n}</p>
                <p className="mt-2 font-medium text-stone-100">{title}</p>
                <p className="mt-1 text-sm text-stone-400 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Weight-loss value props ─────────────────────────────────────── */}
      <section id="trust" className="border-y border-stone-800 bg-stone-950/60">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <h2 className="text-3xl md:text-4xl font-serif leading-tight max-w-2xl">
            {c.valTitle}
          </h2>

          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {c.vals.map(([title, body], i) => (
              <TrustBlock key={title} icon={[Lock, Clock, BadgeCheck][i]} title={title} body={body} />
            ))}
          </div>

          <div className="mt-14 rounded-2xl border border-stone-800 bg-stone-900/40 px-6 py-5 flex items-start gap-4">
            <ShieldCheck className="h-6 w-6 shrink-0 text-alfred-gold" aria-hidden="true" />
            <p className="text-sm text-stone-300 leading-relaxed">
              {t(lang, 'regulatoryBlurb')}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

function TrustBlock({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-stone-800 bg-stone-900/40 p-5">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-alfred-gold" aria-hidden="true" />
        <p className="font-medium text-stone-100">{title}</p>
      </div>
      <p className="mt-2 text-sm text-stone-400 leading-relaxed">{body}</p>
    </div>
  );
}
